import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ProfileTabSVG from '../components/ui/ProfileTabSVG';
import { FloatingNavBar } from '../components/Navbar';
import ProfileEdit from '../components/ProfileEdit';
import HospitalProfileEdit from '../components/HospitalProfileEdit';
import FleetProfileEdit from '../components/FleetProfileEdit';
import ChangePassword from '../components/ChangePassword';
import { useAuth } from '../contexts/AuthContext';
import { useHospitalProfile } from '../hooks/useHospital';
import { useFleetProfile } from '../hooks/useFleet';
import { Loader2 } from 'lucide-react';

/**
 * ProfileScreen - A responsive profile screen component matching the Figma design exactly
 * Features glassmorphism design with user info and profile options
 */
const ProfileScreen = ({
  user,
  onEditProfile,
  onWalletClick,
  onFeedbackClick,
  onPasswordChangeClick,
  onTermsClick,
  onAboutClick,
  onNotificationClick,
  className = '',
  ...props
}) => {
  const { user: authUser } = useAuth();
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  // Check if user is a hospital user
  const isHospitalUser = authUser?.role === 'HOSPITAL_USER' ||
                          authUser?.role === 'hospital' ||
                          authUser?.role === 'Hospital';

  // Check if user is a fleet user
  const isFleetUser = authUser?.role === 'TENANT_ADMIN' ||
                      authUser?.role === 'TENANT_USER';

  // Fetch only the profile for the current user type (hospital endpoint not hit on fleet dashboard)
  const hospitalProfile = useHospitalProfile(isHospitalUser);
  const fleetProfile = useFleetProfile(isFleetUser);

  // Determine which profile to use based on user type
  const { data: profileData, loading: profileLoading, error: profileError } = React.useMemo(() => {
    if (isHospitalUser) {
      return hospitalProfile;
    } else if (isFleetUser) {
      return fleetProfile;
    }
    return { data: null, loading: false, error: null };
  }, [isHospitalUser, isFleetUser, hospitalProfile, fleetProfile]);
  
  // Helper function to generate initials from name (ignoring titles like Dr, Mr, Mrs, Ms, etc.)
  const generateInitials = (name) => {
    if (!name) return 'U';
    
    // Common titles to ignore
    const titles = ['dr', 'mr', 'mrs', 'ms', 'prof', 'professor', 'sir', 'madam', 'miss'];
    
    // Split name by spaces and filter out titles
    const nameParts = name
      .split(' ')
      .map(part => part.trim())
      .filter(part => {
        const lowerPart = part.toLowerCase().replace(/[.,]/g, ''); // Remove punctuation
        return part.length > 0 && !titles.includes(lowerPart);
      });
    
    // If no valid name parts after filtering, return 'U'
    if (nameParts.length === 0) return 'U';
    
    // Generate initials from remaining name parts
    const initials = nameParts
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return initials || 'U';
  };
  
  // Determine which user data to display - prioritize API data, then authUser, then prop user
  const displayUser = React.useMemo(() => {
    // Priority 1: API profile data (for hospital users)
    if (isHospitalUser && profileData?.success && profileData?.data) {
      const apiProfileData = profileData.data;
      return {
        name: apiProfileData.name || authUser?.name || 'Hospital User',
        email: apiProfileData.contact_person_email || apiProfileData.email || authUser?.email || '',
        phone: apiProfileData.hospital_user_phone || apiProfileData.contact_person_phone || apiProfileData.phone || authUser?.phone || authUser?.contact || '',
        smartId: apiProfileData.user_id || authUser?.user_id || authUser?.id || '',
        initials: generateInitials(apiProfileData.name || authUser?.name),
        avatar: null,
        role: apiProfileData.role || authUser?.role || 'HOSPITAL_USER',
        operator_id: apiProfileData.operator_id || authUser?.operator_id || '',
        first_login_completed: apiProfileData.first_login_completed ?? authUser?.first_login_completed ?? false
      };
    }

    // Priority 2: API profile data (for fleet users)
    if (isFleetUser && profileData?.success && profileData?.data) {
      const apiProfileData = profileData.data;
      return {
        name: apiProfileData.name || authUser?.name || 'Fleet Operator',
        email: apiProfileData.email || authUser?.email || '',
        phone: apiProfileData.phone || apiProfileData.contact || authUser?.phone || authUser?.contact || '',
        smartId: apiProfileData.user_id || apiProfileData.operator_id || authUser?.user_id || authUser?.id || '',
        initials: generateInitials(apiProfileData.name || authUser?.name),
        avatar: null,
        role: apiProfileData.role || authUser?.role || 'TENANT_ADMIN',
        operator_id: apiProfileData.operator_id || authUser?.operator_id || '',
        first_login_completed: apiProfileData.first_login_completed ?? authUser?.first_login_completed ?? false
      };
    }
    
    // Priority 3: authUser data (from login)
    if (authUser) {
      return {
        name: authUser.name || authUser.username || 'User',
        email: authUser.email || '',
        phone: authUser.phone || authUser.contact || '',
        smartId: authUser.user_id || authUser.id || authUser.userId || '',
        initials: generateInitials(authUser.name || authUser.username),
        avatar: authUser.avatar || null,
        role: authUser.role || '',
        operator_id: authUser.operator_id || '',
        first_login_completed: authUser.first_login_completed ?? false
      };
    }
    
    // Priority 4: prop user (if provided)
    if (user) {
      return {
        name: user.name || 'User',
        email: user.email || '',
        phone: user.phone || '',
        smartId: user.smartId || user.id || '',
        initials: user.initials || generateInitials(user.name),
        avatar: user.avatar || null,
        role: user.role || '',
        operator_id: user.operator_id || '',
        first_login_completed: user.first_login_completed ?? false
      };
    }
    
    // Fallback: empty user
    return {
      name: 'User',
      email: '',
      phone: '',
      smartId: '',
      initials: 'U',
      avatar: null,
      role: '',
      operator_id: '',
      first_login_completed: false
    };
  }, [isHospitalUser, isFleetUser, profileData, authUser, user]);

  const handleOptionClick = (option) => {
    switch(option) {
      case 'wallet':
        if (onWalletClick) onWalletClick();
        break;
      case 'feedback':
        if (onFeedbackClick) onFeedbackClick();
        break;
      case 'password':
        setShowChangePassword(true);
        if (onPasswordChangeClick) onPasswordChangeClick();
        break;
      case 'terms':
        if (onTermsClick) onTermsClick();
        break;
      case 'about':
        if (onAboutClick) onAboutClick();
        break;
      default:
        break;
    }
  };

  const handleEditProfileClick = () => {
    setShowProfileEdit(true);
    // Removed onEditProfile() call to prevent notification from appearing
  };

  const handleBackToProfile = () => {
    setShowProfileEdit(false);
  };

  const handleBackFromChangePassword = () => {
    setShowChangePassword(false);
  };

  // Render ChangePassword as modal overlay
  const renderChangePasswordModal = () => {
    if (!showChangePassword) return null;
    return <ChangePassword onBackToProfile={handleBackFromChangePassword} />;
  };

  // If ProfileEdit is shown, render it instead of the profile screen
  if (showProfileEdit) {
    return (
      <div className={`w-full h-full relative ${className}`} {...props}>
        {isHospitalUser ? (
          <HospitalProfileEdit onBackToProfile={handleBackToProfile} />
        ) : isFleetUser ? (
          <FleetProfileEdit onBackToProfile={handleBackToProfile} />
        ) : (
          <ProfileEdit onBackToProfile={handleBackToProfile} userType="general" />
        )}
      </div>
    );
  }

  // Show loading state while fetching profile (but still show authUser data if available)
  if ((isHospitalUser || isFleetUser) && profileLoading && !authUser) {
    return (
      <div className={`w-full h-full relative flex items-center justify-center ${className}`} {...props}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profile fetch failed (but still show authUser data if available)
  // Note: We don't return early here - we'll show the error message but still display authUser data

  return (
    <div 
      className={`w-full h-full relative z-0 px-4 sm:px-6 md:px-8 ${className}`}
      {...props}
    >
       {/* ProfileTabSVG Component */}
       <div className="mb-8 flex justify-start">
         <ProfileTabSVG
           width={480}
           height={64}
           userName={displayUser.name}
           userInitials={displayUser.initials}
           showUserName={true}
           onClick={handleEditProfileClick}
           className="hover:scale-105 transition-transform duration-300"
         />
       </div>

       {/* Error message if profile fetch failed */}
       {(isHospitalUser || isFleetUser) && profileError && (
         <div className="mb-6 p-4 bg-red-50/80 border border-red-200/60 rounded-lg backdrop-blur-sm">
           <p className="text-sm text-red-700 font-medium">Failed to load profile details: {profileError.message || 'Please try again later'}</p>
           <p className="text-xs text-red-600 mt-1.5">Showing login information instead</p>
         </div>
       )}

       {/* Hospital Profile Details - Show for all hospital users (API data or authUser fallback) */}
       {isHospitalUser && (
         <div className="mb-8 p-6 bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-xl border border-gray-200/60 shadow-lg">
           <div className="space-y-4">
             {/* Show User ID prominently (this is the login ID) */}
             {displayUser.smartId && (
               <div className="flex items-center justify-between pb-4 border-b border-gray-300/60">
                 <span className="text-sm font-semibold text-gray-700">User ID</span>
                 <span className="text-sm text-gray-900 font-mono font-semibold bg-gray-100/80 px-4 py-1.5 rounded-md border border-gray-200/50">{displayUser.smartId}</span>
               </div>
             )}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {displayUser.name && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</span>
                   <span className="text-base text-gray-900 font-medium">{displayUser.name}</span>
                 </div>
               )}
               {displayUser.email && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</span>
                   <span className="text-base text-gray-900 break-words">{displayUser.email}</span>
                 </div>
               )}
               {displayUser.phone && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</span>
                   <span className="text-base text-gray-900">{displayUser.phone}</span>
                 </div>
               )}
               {displayUser.operator_id && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Operator ID</span>
                   <span className="text-base text-gray-900 font-mono break-all">{displayUser.operator_id}</span>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

       {/* Fleet Profile Details - Show for all fleet users (API data or authUser fallback) */}
       {isFleetUser && (
         <div className="mb-8 p-6 bg-gradient-to-br from-green-50/90 to-white/70 backdrop-blur-md rounded-xl border border-green-200/60 shadow-lg">
           <div className="space-y-4">
             {/* Show User ID prominently (this is the login ID) */}
             {displayUser.smartId && (
               <div className="flex items-center justify-between pb-4 border-b border-green-300/60">
                 <span className="text-sm font-semibold text-gray-700">Fleet Operator ID</span>
                 <span className="text-sm text-gray-900 font-mono font-semibold bg-green-100/80 px-4 py-1.5 rounded-md border border-green-200/50">{displayUser.smartId}</span>
               </div>
             )}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {displayUser.name && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Fleet Name</span>
                   <span className="text-base text-gray-900 font-medium">{displayUser.name}</span>
                 </div>
               )}
               {displayUser.email && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</span>
                   <span className="text-base text-gray-900 break-words">{displayUser.email}</span>
                 </div>
               )}
               {displayUser.phone && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</span>
                   <span className="text-base text-gray-900">{displayUser.phone}</span>
                 </div>
               )}
               {displayUser.operator_id && (
                 <div className="flex flex-col gap-1.5">
                   <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Operator ID</span>
                   <span className="text-base text-gray-900 font-mono break-all">{displayUser.operator_id}</span>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}

      {/* Main Profile Card - matches the Figma design exactly */}
      
      {/* Profile Options Section */}
      <div className="mt-8 space-y-3">
        <button
          type="button"
          className="w-full text-left text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200 py-2 px-1 rounded-md hover:bg-gray-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          onClick={() => handleOptionClick('wallet')}
        >
          Wallet
        </button>
        
        <button
          type="button"
          className="w-full text-left text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200 py-2 px-1 rounded-md hover:bg-gray-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          onClick={() => handleOptionClick('feedback')}
        >
          Feedback & Support
        </button>
        
        <button
          type="button"
          className="w-full text-left text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200 py-2 px-1 rounded-md hover:bg-gray-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          onClick={() => handleOptionClick('password')}
        >
          Change Password
        </button>
        
        <button
          type="button"
          className="w-full text-left text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200 py-2 px-1 rounded-md hover:bg-gray-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          onClick={() => handleOptionClick('terms')}
        >
          Terms & Conditions
        </button>
        
        <button
          type="button"
          className="w-full text-left text-lg font-semibold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200 py-2 px-1 rounded-md hover:bg-gray-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          onClick={() => handleOptionClick('about')}
        >
          About Us
        </button>
      </div>

      {/* Floating Navigation Bar */}
      <FloatingNavBar
        tabs={['Profile', 'Notifications', 'Edit Profile']}
        activeTab="Profile"
        onTabChange={(tab) => {
          console.log(`Switched to ${tab} tab`);
          // Handle tab changes here
          switch(tab) {
            case 'Notifications':
              if (onNotificationClick) onNotificationClick();
              break;
            case 'Edit Profile':
              handleEditProfileClick();
              break;
            default:
              break;
          }
        }}
        showLogout={false}
        className="mt-10 mb-4"
      />

      {/* Change Password Modal Overlay */}
      {renderChangePasswordModal()}
    </div>
  );
};

ProfileScreen.propTypes = {
  /** User information object */
  user: PropTypes.shape({
    name: PropTypes.string,
    smartId: PropTypes.string,
    initials: PropTypes.string,
    avatar: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    operator_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_login_completed: PropTypes.bool
  }),
  /** Edit profile handler */
  onEditProfile: PropTypes.func,
  /** Wallet click handler */
  onWalletClick: PropTypes.func,
  /** Feedback click handler */
  onFeedbackClick: PropTypes.func,
  /** Password change click handler */
  onPasswordChangeClick: PropTypes.func,
  /** Terms click handler */
  onTermsClick: PropTypes.func,
  /** About click handler */
  onAboutClick: PropTypes.func,
  /** Notification click handler */
  onNotificationClick: PropTypes.func,
  /** Additional CSS classes */
  className: PropTypes.string
};

export default ProfileScreen;
